import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { Template } from '@/services/supabase/templates';
import { Check, LayoutTemplate, Search } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

interface PrismEntityDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: { name: string; description: string; template_id?: string; status?: string }) => Promise<void>;
    entity?: {
        id: number;
        name: string;
        description: string | null;
        template_id: number;
        status: string;
    };
    templates: Template[];
    categoryName?: string;
}

export const PrismEntityDialog: React.FC<PrismEntityDialogProps> = ({
    isOpen,
    onClose,
    onSave,
    entity,
    templates,
    categoryName = 'Entity'
}) => {
    const { toast } = useToast();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [templateId, setTemplateId] = useState<string>('');
    const [status, setStatus] = useState<string>('active');
    const [isSaving, setIsSaving] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        if (entity) {
            setName(entity.name);
            setDescription(entity.description || '');
            setTemplateId(entity.template_id.toString());
            setStatus(entity.status);
        } else {
            if (!isOpen) {
                setTimeout(() => { setName(''); setDescription(''); setStatus('active'); setSearchQuery(''); }, 300);
            }
            if (isOpen && !entity && templates.length > 0 && !templateId) {
                setTemplateId(templates[0].id.toString());
            }
        }
    }, [entity, templates, isOpen, templateId]);

    const activeTemplate = useMemo(() => templates.find(t => t.id.toString() === templateId), [templateId, templates]);

    const filteredTemplates = useMemo(() => {
        if (!searchQuery.trim()) return templates;
        return templates.filter(t =>
            t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (t.description && t.description.toLowerCase().includes(searchQuery.toLowerCase()))
        );
    }, [templates, searchQuery]);

    const handleSave = async () => {
        if (!name.trim()) return toast({ title: "Validation Error", description: "Name is required.", variant: "destructive" });
        if (!templateId && !entity) return toast({ title: "Validation Error", description: "Please select a template.", variant: "destructive" });

        try {
            setIsSaving(true);
            await onSave({ name, description, template_id: templateId, status });
            onClose();
        } catch (error) {
            console.error('Error saving:', error);
            toast({ title: "Error", description: "Failed to save. Please try again.", variant: "destructive" });
        } finally {
            setIsSaving(false);
        }
    };

    const isEditing = !!entity;

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="sm:max-w-[900px] sm:rounded-2xl p-0 overflow-hidden gap-0 border-border/60 shadow-2xl">
                <DialogHeader className="p-8 pb-6 border-b border-border/40 bg-muted/5 space-y-1">
                    <DialogTitle className="text-2xl font-semibold tracking-tight text-foreground">
                        {isEditing ? `Edit ${categoryName}` : `Create New ${categoryName}`}
                    </DialogTitle>
                    <DialogDescription className="text-base text-muted-foreground leading-relaxed">
                        {isEditing
                            ? "Update the details for this entity below."
                            : "Choose a template and define your new entity."}
                    </DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-8">
                    {/* Left Column: Form Inputs */}
                    <div className="space-y-6">
                        <div className="space-y-2.5">
                            <Label htmlFor="name" className="text-sm font-medium text-foreground/80 ml-1">
                                Name
                            </Label>
                            <Input
                                id="name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder={`e.g. ${categoryName === 'Character' ? 'Sherlock Holmes' : 'New Entity'}`}
                                className="h-11 rounded-xl bg-muted/30 border-muted-foreground/10 focus:border-primary/20 focus:ring-primary/20 transition-all font-medium text-foreground placeholder:text-muted-foreground/50 shadow-sm"
                                autoFocus
                            />
                        </div>

                        <div className="space-y-2.5">
                            <Label htmlFor="description" className="text-sm font-medium text-foreground/80 ml-1">
                                Description
                            </Label>
                            <Textarea
                                id="description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Add a brief description..."
                                className="min-h-[160px] rounded-xl bg-muted/30 border-muted-foreground/10 focus:border-primary/20 focus:ring-primary/20 resize-none transition-all font-medium text-foreground placeholder:text-muted-foreground/50 shadow-sm leading-relaxed"
                            />
                        </div>

                        {/* Selected Template Summary (Mobile/Small Screen Context) */}
                        <div className="p-4 rounded-xl bg-muted/30 border border-muted-foreground/10 space-y-2">
                            <div className="flex items-center gap-2 text-primary/80">
                                <LayoutTemplate className="w-4 h-4" />
                                <span className="text-xs font-bold uppercase tracking-wider">Selected Template</span>
                            </div>
                            {activeTemplate ? (
                                <div className="space-y-1">
                                    <div className="font-semibold text-sm text-foreground">{activeTemplate.name}</div>
                                    <div className="text-xs text-muted-foreground line-clamp-2">{activeTemplate.description}</div>
                                </div>
                            ) : (
                                <div className="text-sm text-muted-foreground italic">No template selected</div>
                            )}
                        </div>
                    </div>

                    {/* Right Column: Template Selection */}
                    <div className="space-y-2.5 flex flex-col">
                        <div className="flex items-center justify-between ml-1">
                            <Label className="text-sm font-medium text-foreground/80">
                                Select Template
                            </Label>
                            <span className="text-[10px] uppercase font-bold text-muted-foreground/70 bg-muted/50 px-2 py-0.5 rounded-full">
                                {filteredTemplates.length} Available
                            </span>
                        </div>

                        <div className="h-[400px] rounded-xl bg-muted/30 border border-muted-foreground/10 p-4 shadow-sm overflow-hidden flex flex-col gap-3">
                            {/* Search */}
                            <div className="relative shrink-0">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/70" />
                                <Input
                                    placeholder="Search templates..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-9 h-9 rounded-lg bg-background/50 border-muted-foreground/10 focus:bg-background transition-all text-sm"
                                />
                            </div>

                            {/* Template List */}
                            <div className="flex-1 overflow-y-auto scrollbar-hide -mr-2 pr-2 space-y-2">
                                {filteredTemplates.map(t => {
                                    const isSelected = t.id.toString() === templateId;
                                    return (
                                        <button
                                            key={t.id}
                                            onClick={() => setTemplateId(t.id.toString())}
                                            disabled={isEditing} // Template cannot be changed in edit mode usually, preserving logic
                                            className={cn(
                                                "w-full flex flex-col items-start gap-1 p-3 rounded-lg border text-left transition-all relative group",
                                                isSelected
                                                    ? "bg-background shadow-md border-primary/40 ring-1 ring-primary/20"
                                                    : "bg-background/40 border-transparent hover:bg-background hover:shadow-sm hover:border-border/60",
                                                isEditing && "opacity-60 cursor-not-allowed"
                                            )}
                                        >
                                            <div className="flex items-center justify-between w-full">
                                                <span className={cn(
                                                    "text-sm font-semibold",
                                                    isSelected ? "text-primary" : "text-foreground/80 group-hover:text-foreground"
                                                )}>
                                                    {t.name}
                                                </span>
                                                {isSelected && <Check className="w-3.5 h-3.5 text-primary" />}
                                            </div>
                                            <p className="text-xs text-muted-foreground/80 line-clamp-2 leading-relaxed">
                                                {t.description || "No description provided."}
                                            </p>
                                        </button>
                                    )
                                })}
                                {filteredTemplates.length === 0 && (
                                    <div className="py-8 text-center text-sm text-muted-foreground border border-dashed border-border/50 rounded-lg bg-background/20">
                                        No templates found.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-6 bg-muted/5 border-t border-border/40 flex justify-end gap-3">
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={onClose}
                        disabled={isSaving}
                        className="min-w-[100px] rounded-xl hover:bg-muted/50"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        onClick={handleSave}
                        disabled={isSaving}
                        className={cn(
                            "min-w-[140px] rounded-xl font-medium shadow-sm active:scale-95 transition-all text-base",
                            isSaving && "opacity-80"
                        )}
                    >
                        {isSaving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Entity')}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};
